import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import RGBParade from './components/RGBParade'
import LumaScope from './components/LumaScope'
import ZoomBox from './components/ZoomBox'
import FalseColor from './components/FalseColor'
import Vectorscope from './components/Vectorscope'
import './App.css'

function SortableScopeItem({ id, children, is2X, isDragging }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isSortableDragging ? 0.5 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`scope-container ${is2X ? 'scope-2x' : ''} ${isSortableDragging ? 'dragging' : ''}`}
    >
      <div className="drag-handle" {...attributes} {...listeners} title="Drag to reorder">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
          <circle cx="4" cy="4" r="1.5"/>
          <circle cx="12" cy="4" r="1.5"/>
          <circle cx="4" cy="8" r="1.5"/>
          <circle cx="12" cy="8" r="1.5"/>
          <circle cx="4" cy="12" r="1.5"/>
          <circle cx="12" cy="12" r="1.5"/>
        </svg>
      </div>
      {children}
    </div>
  )
}

function App() {
  const videoRef = useRef(null)
  const imageRef = useRef(null)
  const streamRef = useRef(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [error, setError] = useState(null)
  const [devices, setDevices] = useState([])
  const [selectedDeviceId, setSelectedDeviceId] = useState(null)
  const [useSMPTE, setUseSMPTE] = useState(false)
  const [scopeOrder, setScopeOrder] = useState([
    'video',
    'rgbParade',
    'lumaScope',
    'zoomBox',
    'falseColor',
    'vectorscope'
  ])
  const [reloadKey, setReloadKey] = useState(0)
  const [activeId, setActiveId] = useState(null)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )
  
  // Scope visibility toggles (default: Luma and RGB Parade on)
  const [scopeVisibility, setScopeVisibility] = useState({
    rgbParade: true,
    lumaScope: true,
    zoomBox: false,
    falseColor: false,
    vectorscope: false
  })

  // 2X mode toggles (full width on desktop)
  const [scope2X, setScope2X] = useState({
    video: false,
    rgbParade: false,
    lumaScope: false,
    zoomBox: false,
    falseColor: false,
    vectorscope: false
  })

  // Enumerate available video devices (run once on mount)
  useEffect(() => {
    const enumerateDevices = async () => {
      try {
        // Request permission first by getting a stream
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false })
        stream.getTracks().forEach(track => track.stop()) // Stop to release
        
        // Now enumerate devices
        const deviceList = await navigator.mediaDevices.enumerateDevices()
        const videoDevices = deviceList.filter(device => device.kind === 'videoinput')
        setDevices(videoDevices)
        
        // Only set initial device if none is selected (initial mount only)
        setSelectedDeviceId((prev) => {
          if (!prev && videoDevices.length > 0) {
            return videoDevices[0].deviceId
          }
          return prev
        })
      } catch (err) {
        console.error('Error enumerating devices:', err)
        setError('Unable to access camera devices. Please check permissions.')
      }
    }

    enumerateDevices()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Initialize webcam with selected device
  useEffect(() => {
    if (useSMPTE || !selectedDeviceId) return

    const initWebcam = async () => {
      try {
        // Stop previous stream
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop())
        }

        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            deviceId: { exact: selectedDeviceId },
            width: { ideal: 1920 },
            height: { ideal: 1080 }
          },
          audio: false
        })
        
        streamRef.current = stream
        const video = videoRef.current
        if (video) {
          video.srcObject = stream
          video.play()
          setIsPlaying(true)
          setError(null)
        }
      } catch (err) {
        console.error('Error accessing webcam:', err)
        setError('Unable to access selected camera. Please check permissions.')
      }
    }

    initWebcam()

    return () => {
      // Cleanup: stop all tracks when component unmounts or device changes
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop())
      }
    }
  }, [selectedDeviceId, useSMPTE])

  // Load SMPTE color bars image
  useEffect(() => {
    if (!useSMPTE) return

    const image = imageRef.current
    if (image) {
      image.src = '/smpte-color-bars.png'
      image.onload = () => {
        setIsPlaying(true)
        setError(null)
      }
      image.onerror = () => {
        setError('Unable to load SMPTE color bars image.')
      }
    }

    // Stop video stream when switching to SMPTE
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
      streamRef.current = null
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null
    }
  }, [useSMPTE])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const handlePlay = () => setIsPlaying(true)
    const handlePause = () => setIsPlaying(false)

    video.addEventListener('play', handlePlay)
    video.addEventListener('pause', handlePause)

    return () => {
      video.removeEventListener('play', handlePlay)
      video.removeEventListener('pause', handlePause)
    }
  }, [])

  // Restart video after reordering (when reloadKey changes)
  useEffect(() => {
    if (useSMPTE || !selectedDeviceId) return

    // Use a small timeout to ensure the video element is mounted and ref is set
    const timeoutId = setTimeout(() => {
      const video = videoRef.current
      const stream = streamRef.current

      // If we have a stream and the video element exists, restart playback
      if (stream && video) {
        // Reattach stream to ensure it's connected
        if (video.srcObject !== stream) {
          video.srcObject = stream
        }
        // Restart playback
        video.play().catch(err => {
          console.error('Error restarting video:', err)
        })
      }
    }, 50)

    return () => clearTimeout(timeoutId)
  }, [reloadKey, useSMPTE, selectedDeviceId])

  const handleDeviceChange = useCallback((e) => {
    setSelectedDeviceId(e.target.value)
  }, [])

  const handleDragStart = useCallback((event) => {
    setActiveId(event.active.id)
  }, [])

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      setScopeOrder((items) => {
        const oldIndex = items.indexOf(active.id)
        const newIndex = items.indexOf(over.id)
        const newOrder = arrayMove(items, oldIndex, newIndex)
        // Trigger reload of scopes after reordering
        setReloadKey((prev) => prev + 1)
        return newOrder
      })
    }

    setActiveId(null)
  }, [])

  const commonProps = useMemo(() => ({
    videoRef,
    imageRef,
    useSMPTE,
    seekTime: 0
  }), [useSMPTE])

  const toggle2X = useCallback((scopeId) => {
    setScope2X((prev) => ({
      ...prev,
      [scopeId]: !prev[scopeId]
    }))
  }, [])

  const renderScope = useCallback((scopeId, reloadKey) => {
    const is2X = scope2X[scopeId] || false
    const toggle2XHandler = () => toggle2X(scopeId)

    switch (scopeId) {
      case 'video':
        return (
          <div key={`video-${reloadKey}`} className="video-container">
            {error ? (
              <div className="webcam-error">
                <p>{error}</p>
                <button onClick={() => window.location.reload()} className="retry-button">
                  Retry
                </button>
              </div>
            ) : (
              <>
                {useSMPTE ? (
                  <img
                    ref={imageRef}
                    className="video-player"
                    alt="SMPTE Color Bars"
                    style={{ display: 'block', width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                ) : (
                  <video
                    key={`video-element-${reloadKey}`}
                    ref={videoRef}
                    className="video-player"
                    autoPlay
                    playsInline
                    muted
                  />
                )}
                <div className="video-controls">
                  <div className="video-toggle">
                    <button
                      className={`toggle-button ${useSMPTE ? 'active' : ''}`}
                      onClick={() => setUseSMPTE(!useSMPTE)}
                    >
                      {useSMPTE ? 'LIVE VIDEO' : 'SMPTE BARS'}
                    </button>
                  </div>
                  {!useSMPTE && (
                    <div className="device-selector">
                      <label htmlFor="camera-select" className="device-label">
                        Camera:
                      </label>
                      <select
                        id="camera-select"
                        value={selectedDeviceId || ''}
                        onChange={handleDeviceChange}
                        className="device-select"
                      >
                        {devices.map(device => (
                          <option key={device.deviceId} value={device.deviceId}>
                            {device.label || `Camera ${device.deviceId.slice(0, 8)}`}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <button
                    className={`toggle-button ${is2X ? 'active' : ''}`}
                    onClick={toggle2XHandler}
                    title="2X Width (Desktop only)"
                  >
                    2X
                  </button>
                </div>
              </>
            )}
          </div>
        )
      case 'rgbParade':
        return <RGBParade key={`rgbParade-${reloadKey}`} {...commonProps} is2X={is2X} onToggle2X={toggle2XHandler} />
      case 'lumaScope':
        return <LumaScope key={`lumaScope-${reloadKey}`} {...commonProps} is2X={is2X} onToggle2X={toggle2XHandler} />
      case 'zoomBox':
        return <ZoomBox key={`zoomBox-${reloadKey}`} {...commonProps} is2X={is2X} onToggle2X={toggle2XHandler} />
      case 'falseColor':
        return <FalseColor key={`falseColor-${reloadKey}`} {...commonProps} is2X={is2X} onToggle2X={toggle2XHandler} />
      case 'vectorscope':
        return <Vectorscope key={`vectorscope-${reloadKey}`} {...commonProps} is2X={is2X} onToggle2X={toggle2XHandler} />
      default:
        return null
    }
  }, [commonProps, error, devices, selectedDeviceId, useSMPTE, handleDeviceChange, reloadKey, scope2X, toggle2X])

  const toggleScope = useCallback((scopeId) => {
    setScopeVisibility((prev) => ({
      ...prev,
      [scopeId]: !prev[scopeId]
    }))
  }, [])

  // Filter scope order to only include visible scopes (video is always visible)
  const visibleScopeOrder = useMemo(() => {
    return scopeOrder.filter((scopeId) => {
      if (scopeId === 'video') return true
      return scopeVisibility[scopeId] === true
    })
  }, [scopeOrder, scopeVisibility])

  return (
    <div className="app">
      <header className="app-header">
        <h1>SCOPENATOR</h1>
        <div className="scope-toggles">
          <button
            className={`scope-toggle ${scopeVisibility.zoomBox ? 'active' : ''}`}
            onClick={() => toggleScope('zoomBox')}
            title="2x Preview (2x center crop)"
          >
            2x Preview
          </button>
          <button
            className={`scope-toggle ${scopeVisibility.falseColor ? 'active' : ''}`}
            onClick={() => toggleScope('falseColor')}
            title="False Color"
          >
            False Color
          </button>
          <button
            className={`scope-toggle ${scopeVisibility.vectorscope ? 'active' : ''}`}
            onClick={() => toggleScope('vectorscope')}
            title="Vectorscope"
          >
            Vectorscope
          </button>
          <button
            className={`scope-toggle ${scopeVisibility.rgbParade ? 'active' : ''}`}
            onClick={() => toggleScope('rgbParade')}
            title="RGB Parade"
          >
            RGB Parade
          </button>
          <button
            className={`scope-toggle ${scopeVisibility.lumaScope ? 'active' : ''}`}
            onClick={() => toggleScope('lumaScope')}
            title="Luma"
          >
            Luma
          </button>
        </div>
      </header>
      <div className="app-content">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={visibleScopeOrder} strategy={rectSortingStrategy}>
            <div className="scopes-grid">
              {visibleScopeOrder.map((scopeId) => {
                const is2X = scope2X[scopeId] || false
                return (
                  <SortableScopeItem key={scopeId} id={scopeId} is2X={is2X}>
                    {renderScope(scopeId, reloadKey)}
                  </SortableScopeItem>
                )
              })}
            </div>
          </SortableContext>
          <DragOverlay>
            {activeId ? (
              <div className="scope-container dragging" style={{ opacity: 0.8 }}>
                <div className="drag-handle" title="Drag to reorder">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <circle cx="4" cy="4" r="1.5"/>
                    <circle cx="12" cy="4" r="1.5"/>
                    <circle cx="4" cy="8" r="1.5"/>
                    <circle cx="12" cy="8" r="1.5"/>
                    <circle cx="4" cy="12" r="1.5"/>
                    <circle cx="12" cy="12" r="1.5"/>
                  </svg>
                </div>
                {renderScope(activeId, reloadKey)}
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>
    </div>
  )
}

export default App

